import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { trackPageView, syncStoredAnalyticsEvents } from './services/analyticsService';
import { initializeGA, trackGAPageView } from './services/googleAnalytics';
import { supabase } from '@/integrations/supabase/client';
import SEOHead from './components/SEOHead';
import Index from './pages/Index';
import GamePage from './pages/GamePage';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import ErrorBoundary from './components/ErrorBoundary';

// Initialize React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Analytics route tracking component
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view in internal analytics
    trackPageView(location.pathname);
    
    // Track page view in Google Analytics
    trackGAPageView(location.pathname);
  }, [location]);
  
  // Sync stored analytics on mount (useful after user logs in)
  useEffect(() => {
    syncStoredAnalyticsEvents();
  }, []);
  
  return (
    <SEOHead 
      title={getPageTitle(location.pathname)} 
      description={getPageDescription(location.pathname)} 
    />
  );
};

// Helper function to determine title based on route
const getPageTitle = (pathname: string): string => {
  if (pathname === '/') return 'Home';
  if (pathname.includes('/game/')) return 'Playing Game';
  if (pathname === '/profile') return 'User Profile';
  if (pathname === '/terms') return 'Terms of Service';
  if (pathname === '/privacy') return 'Privacy Policy';
  return 'Page Not Found';
};

// Helper function to determine description based on route
const getPageDescription = (pathname: string): string => {
  if (pathname === '/') 
    return 'Overcome cravings with mindful games designed to help you beat addictive urges. Use our SOS button when cravings strike.';
  if (pathname.includes('/game/'))
    return 'Focus on this mindfulness game to help overcome your current craving.';
  if (pathname === '/profile')
    return 'View your progress and stats in managing cravings with Crave SOS Games.';
  if (pathname === '/terms')
    return 'Terms of Service for using Crave SOS Games platform.';
  if (pathname === '/privacy')
    return 'Privacy Policy detailing how we handle your data at Crave SOS Games.';
  return 'Crave SOS Games - Tools to help overcome cravings and addictive urges.';
};

function App() {
  // Initialize Google Analytics on app load
  useEffect(() => {
    initializeGA();
  }, []);

  return (
    <ErrorBoundary>
      <SessionContextProvider supabaseClient={supabase}>
        <QueryClientProvider client={queryClient}>
          <Router>
            <RouteTracker />
            <Routes>
              <Route path="/" element={
                <ErrorBoundary>
                  <Index />
                </ErrorBoundary>
              } />
              {/* Updated routes for Game - supporting both ID and slug-based URLs */}
              <Route path="/game/:gameSlug" element={
                <ErrorBoundary>
                  <GamePage />
                </ErrorBoundary>
              } />
              <Route path="/profile" element={
                <ErrorBoundary>
                  <Profile />
                </ErrorBoundary>
              } />
              <Route path="/terms" element={
                <ErrorBoundary>
                  <Terms />
                </ErrorBoundary>
              } />
              <Route path="/privacy" element={
                <ErrorBoundary>
                  <Privacy />
                </ErrorBoundary>
              } />
              <Route path="*" element={
                <ErrorBoundary>
                  <NotFound />
                </ErrorBoundary>
              } />
            </Routes>
          </Router>
          <Toaster />
        </QueryClientProvider>
      </SessionContextProvider>
    </ErrorBoundary>
  );
}

export default App;
